import 'index.css'
import StyleSheet from './StyleSheet'
import { createMuiTheme } from '@material-ui/core/styles'
import Color from 'color'

export const colorPalette = {
  primary: [
    Color('#6AAEAA'),
    Color('#1B3044'),
    Color('#464646'),
  ],
  secondary: [
    Color('#095084'),
    Color('#5c9693'),
    Color('#90a3b1'),
    Color('#e8e8e8'),
  ],
  tertiary: [
    Color('#b9b39b'),
    Color('#90c814'),
    Color('#f5bd23'),
    Color('#0bdbc1'),
  ]
}

const errorColor = Color('#e94537')

export const baseThemeDefinition = {
  palette: {
    primary: {
      main: colorPalette.primary[1].hex(),
    },
  },
  typography: {
    useNextVariants: true,
    fontFamily: 'Montserrat,sans-serif',
    h5: {
      fontSize: '32px',
      fontWeight: 700,
      color: colorPalette.primary[1].hex(),
      '@media print': {
        fontSize: '26px',
      }
    },
    h4: {
      fontSize: '32px',
      fontWeight: 700,
      color: colorPalette.primary[1].hex()
    },
    body2: {
      fontSize: '14px',
      color: colorPalette.primary[1].hex(),
      '@media print': {
        fontSize: '11px',
      }
    },
    body1: {
      color: colorPalette.primary[1].hex(),
      fontWeight: 500,
      fontSize: '0.875rem'
    },
    h6: {
      fontWeight: 700,
      color: colorPalette.primary[1].hex()
    },
    subtitle1: {
      fontWeight: 700,
      fontSize: '14px',
      color: colorPalette.primary[1].hex(),
      '@media print': {
        fontSize: '11px',
      }
    }
  },
  overrides: {
    MuiButton: {
      root: {
        lineHeight: '12px',
        fontWeight: 500,
        borderRadius: '2px',
        paddingTop: '1em',
        paddingBottom: '1em',
        '&$disabled': {
          color: '#757575'
        }
      },
      contained: {
        boxShadow: 'none',
        '&$disabled': {
          backgroundColor: '#a4a4a4'
        },
        '&:active': {
          boxShadow: 'none',
        }
      },
      raisedPrimary: {
        color: '#ffffff',
        backgroundColor: colorPalette.primary[0].hex(),
        '&:hover': {
          backgroundColor: colorPalette.primary[0].darken(0.1).hex(),
        }
      },
      raisedSecondary: {
        color: '#ffffff',
        backgroundColor: colorPalette.secondary[1].hex(),
        '&:hover': {
          backgroundColor: colorPalette.secondary[1].darken(0.1).hex(),
        }
      },
    },
    MuiMenuItem: {
      root: {
        '&$selected': {
          color: 'white'
        }
      }
    },
    MuiAppBar: {
      root: {
        boxShadow: 'none',
      },
    },
    MuiListItem: {
      root: {
        marginTop: '2px',
        backgroundColor: '#f0f0f0'
      }
    },
    MuiListItemText: {
      primary: {
          lineHeight: '15px',
          fontSize: '16px',
          fontWeight: 700,
          color: colorPalette.primary[2].hex(),
          '&$textDense': {
            fontSize: '16px',
          }
      },
      secondary: {
          lineHeight: '12px',
          fontSize: '14px',
          fontWeight: '500',
          color: colorPalette.primary[2].hex(),
          '&$textDense': {
            fontSize: '14px',
          }
      }
    },
    MuiInput: {
      input: {
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '14px',
        color: '#464646',
        '@media print': {
          fontSize: '12px',
          lineHeight: '11px'
        }
      },
      inputMultiline: {
        lineHeight: '19px'
      },
      error: {
        '& $input': {
          color: errorColor.hex(),
        },
        color: errorColor.hex(),
        '&:after': {
          backgroundColor: errorColor.hex(),
          zIndex: 20
        }
      },
      underline: {
        '&:before': {
          zIndex: 10
        }
      }
    },
    MuiInputLabel: {
      formControl: {
        color: colorPalette.primary[2].hex()
      },
      shrink: {
        height: '9px',
        color: colorPalette.primary[2].hex()
      }
    },
    MuiFormLabel: {
      root: {
        lineHeight: '11px',
        fontSize: '16px'
      },
      error: {
        color: errorColor.hex()
      }
    },
    MuiFormHelperText: {
      error: {
        lineHeight: '9px',
        fontSize: '10px',
        fontWeight: 500,
        color: errorColor.hex()
      }
    },
    MuiRadio: {
      colorSecondary: {
        '&$checked': {
          color: colorPalette.secondary[1].hex()
        }
      }
    },
    // Secondary Nav
    MuiTabs: {
      root: {
        '@media print': {
          display: 'none'
        }
      },
      flexContainer: {
        borderBottom: '1px solid #e8e8e8',
      },
      indicator: {
        height: '5px',
        backgroundColor: colorPalette.primary[0].hex()
      }
    },
    MuiTab: {
      root: {
        textTransform: 'none',
        opacity: 1
      },
      textColorInherit: {
        opacity: 1
      },
      label: {
        '&:hover': {
          color: colorPalette.secondary[1].hex()
        },
        '@media (min-width: 960px)': {
          fontSize: '16px',
        },
        fontSize: '16px',
        lineHeight: '15px',
        fontWeight: 500,
        color: colorPalette.primary[1].hex()
      },
    },
    MuiTooltip: {
      popper: {
        '@media print': {
          display: 'none',
        }
      }
    }
  },
  siteStyleguide: {
    maxWidth: 1180,
    colorPalette, // TODO: we have to map all these values to .hex()
    typography1: {
      h1:{
        fontSize: '85px',
        fontWeight: 700,
        fontStyle: 'normal',
        fontStretch: 'normal',
        lineHeight: 0.94,
        letterSpacing: 'normal',
        textAlign: 'left',
        color: colorPalette.primary[1].hex()
      },
      h2:{
        fontSize: '44px',
        fontWeight: 700,
        fontStyle: 'normal',
        fontStretch: 'normal',
        lineHeight: 1.14,
        letterSpacing: 'normal',
        textAlign: 'left',
        color: colorPalette.primary[1].hex()
      },
      h3:{
        fontSize: '32px',
        fontWeight: 700,
        fontStyle: 'normal',
        fontStretch: 'normal',
        lineHeight: 1.25,
        letterSpacing: 'normal',
        textAlign: 'left',
        color: colorPalette.primary[1].hex()
      },
      h4:{
        fontSize: '26px',
        fontWeight: 700,
        fontStyle: 'normal',
        fontStretch: 'normal',
        lineHeight: 1.23,
        letterSpacing: 'normal',
        textAlign: 'left',
        color: colorPalette.primary[1].hex()
      },
      h5:{
        fontSize: '22px',
        fontWeight: 500,
        fontStyle: 'normal',
        fontStretch: 'normal',
        lineHeight: 1.27,
        letterSpacing: 'normal',
        textAlign: 'left',
        color: colorPalette.primary[1].hex()
      },
      h6:{
        fontSize: '18px',
        fontWeight: 500,
        fontStyle: 'normal',
        fontStretch: 'normal',
        lineHeight: '1.11',
        letterSpacing: 'normal',
        textAlign: 'left',
        color: colorPalette.primary[1].hex()
      },
    }
  }
}

export const baseTheme = createMuiTheme(baseThemeDefinition)

export const darkThemeDefinition = {
  palette: {
    "type": "dark",
  },
  typography: {
    useNextVariants: true,
    fontFamily: 'Montserrat,sans-serif',
    h5: {
      fontSize: '32px',
      fontWeight: 700,
      color: 'white',
      '@media print': {
        fontSize: '26px',
      }
    },
    h4: {
      fontSize: '32px',
      fontWeight: 700,
      color: 'white'
    },
    body2: {
      fontSize: '14px',
      color: 'white',
      '@media print': {
        fontSize: '11px',
      }
    },
    body1: {
      color: 'white',
      fontWeight: 500,
      fontSize: '0.875rem'
    },
    h6: {
      fontWeight: 900,
      fontSize: '27px',
      color: 'white'
    },
    subtitle1: {
      fontWeight: 700,
      fontSize: '14px',
      color: 'white',
      '@media print': {
        fontSize: '11px',
      }
    }
  },
  overrides: {
    MuiInput: {
      formControl: {
        'label + &': {
          marginTop: 10,
        }
      },
      input: {
        fontWeight: 500,
        fontSize: 16,
        '&:-webkit-autofill': {
          '-webkit-box-shadow': '0 0 0 30px #1b3044 inset',
          '-webkit-text-fill-color': 'rgb(250, 255, 189) !important',
          caretColor: 'white'
        }
      },
      disabled: {}
    },
    MuiInputLabel: {
      formControl: {
        transform: 'translate(0, 20px) scale(1)'
      },
    },
    MuiFormLabel: {
      root: {
        ...baseThemeDefinition.overrides.MuiFormLabel.root,
        color: '#cfdbe6',
        '&$focused': {
          color: '#ffffff',

        }
      },
    },
    MuiFormControlLabel: {
      label: {
        color: 'white'
      }
    },
    MuiFormControl: {
      root: {
        verticalAlign: 'middle'
      }
    },
    MuiButton: baseThemeDefinition.overrides.MuiButton,
    MuiDialog: {
      paper: {
        backgroundColor: colorPalette.primary[1].hex()
      }
    },
    MuiCardContent: {
      root: {
        padding: '16px 24px'
      }
    },
    MuiDialogContent: {
      root: {
        flex: '1 0 auto'
      }
    },
    MuiTypography: {
      title: {
        color: '#ffffff',
        fontSize: '27px',
        fontWeight: 900
      },
      subheading: {
        color: 'white',
        paddingTop: 20,
      }
    },
    MuiMenuItem: {
      root: {
        color: 'white',
      }
    },
    MuiPaper: {
      root: {
        backgroundColor: colorPalette.primary[1].hex(),
      },
      elevation1: {
        boxShadow: 'none'
      },
      elevation2: {
        boxShadow: 'none'
      }
    },
    MuiPickersModal: {
      dialogAction: {
        width: 'auto'
      }
    }
  }
}

export const darkTheme = createMuiTheme(darkThemeDefinition)

export default StyleSheet({
  '@global': {
    body: {
      overflowX: 'hidden'
    }
  },
  link: {
    color: 'white',
    textDecoration: 'none',
    outline: 0,
    '&:hover':{
      textDecoration: 'underline'
    }
  }
})